import {
    atom,
    atomFamily,
    CallbackInterface,
    RecoilState,
    RecoilValueReadOnly,
    selector,
    selectorFamily,
} from 'recoil';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import type { Orientations } from '~/engine/view/viewOrientation';
import { KEEP_LATEST_ONLY } from '~/state/atoms/selectorCachePolicy';

/** The series and orientation a tile shows; '' matches nothing. A Recoil param. */
export type AnnotationViewKey = {
    readonly seriesInstanceUid: string;
    readonly orientation: Orientations | '';
};

type AnnotationIndexEntry = AnnotationViewKey & { readonly id: string };

const onSameView = (a: AnnotationViewKey, b: AnnotationViewKey) =>
    a.seriesInstanceUid === b.seriesInstanceUid &&
    a.orientation === b.orientation;

/**
 * The placed annotations of one kind, one atom each. The id list gives the
 * order; an edit writes the one atom it changes, so nothing else
 * changes. An id is in the list exactly while its atom holds a record.
 */
export interface AnnotationListState<T extends AnnotationRecord> {
    /** The id list, with what each was placed on; edits leave it as it is. */
    readonly index: RecoilState<readonly AnnotationIndexEntry[]>;
    /** Ids of the placed annotations, oldest first. */
    readonly ids: RecoilValueReadOnly<readonly string[]>;
    /** Ids of those placed on the series and orientation `view` shows. */
    readonly idsOnView: (
        view: AnnotationViewKey
    ) => RecoilValueReadOnly<readonly string[]>;
    /** One annotation by id; undefined for an id that is not in the list. */
    readonly item: (id: string) => RecoilState<T | undefined>;
    /** Every placed annotation, oldest first, derived from the ids and their atoms. */
    readonly items: RecoilValueReadOnly<readonly T[]>;
    /** The annotation the selection names, when it is one of this list's. */
    readonly selected: RecoilValueReadOnly<T | undefined>;
}

/** The state of one kind of annotation; `key` prefixes its atom keys. */
export function annotationListState<T extends AnnotationRecord>(
    key: string
): AnnotationListState<T> {
    const index = atom<readonly AnnotationIndexEntry[]>({
        key: `${key}IndexState`,
        default: [],
    });
    const ids = selector<readonly string[]>({
        key: `${key}IdsState`,
        get: ({ get }) => get(index).map((entry) => entry.id),
        cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
    });
    const idsOnView = selectorFamily<readonly string[], AnnotationViewKey>({
        key: `${key}IdsOnViewState`,
        get:
            (view) =>
            ({ get }) =>
                get(index)
                    .filter((entry) => onSameView(entry, view))
                    .map((entry) => entry.id),
        cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
    });
    const item = atomFamily<T | undefined, string>({
        key: `${key}ItemState`,
        default: undefined,
    });
    const items = selector<readonly T[]>({
        key: `${key}ItemsState`,
        get: ({ get }) =>
            get(ids).flatMap((id) => {
                const placed = get(item(id));
                return placed === undefined ? [] : [placed];
            }),
        cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
    });
    const selected = selector<T | undefined>({
        key: `${key}SelectedState`,
        get: ({ get }) => {
            const id = get(selectedAnnotationState);
            return id === null ? undefined : get(item(id));
        },
        cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
    });
    return { index, ids, idsOnView, item, items, selected };
}

/** Appends an annotation: its atom is set and its id added, in one update. */
export function addAnnotation<T extends AnnotationRecord>(
    { set }: Pick<CallbackInterface, 'set'>,
    list: AnnotationListState<T>,
    annotation: T
): void {
    const { seriesInstanceUid, orientation } = annotation.plane;
    set(list.item(annotation.id), annotation);
    set(list.index, (index) => [
        ...index,
        { id: annotation.id, seriesInstanceUid, orientation },
    ]);
}

/** Removes one annotation: its id from the list, its atom to the default. */
export function removeAnnotation<T extends AnnotationRecord>(
    { set, reset }: Pick<CallbackInterface, 'set' | 'reset'>,
    list: AnnotationListState<T>,
    id: string
): void {
    set(list.index, (index) => index.filter((entry) => entry.id !== id));
    reset(list.item(id));
}

/**
 * Replaces one annotation with `edit` of it. An id whose atom is unset is
 * left unset, so an edit landing after a removal creates nothing. The edit
 * is applied to the record in the snapshot, so this must not share a
 * callback with an add or another edit of the same annotation.
 * Throws, writing nothing, when the edit moves it to another series or view.
 */
export function updateAnnotation<T extends AnnotationRecord>(
    { snapshot, set }: Pick<CallbackInterface, 'snapshot' | 'set'>,
    list: AnnotationListState<T>,
    id: string,
    edit: (annotation: T) => T
): void {
    const placed = snapshot.getLoadable(list.item(id)).getValue();
    if (placed === undefined) {
        return;
    }
    const edited = edit(placed);
    if (!onSameView(edited.plane, placed.plane)) {
        throw new Error("Edit changed the annotation's series or orientation");
    }
    set(list.item(id), (current) => (current === undefined ? current : edited));
}

/**
 * Removes every annotation: the id list and each atom go back to default.
 * The ids are read from the snapshot, which predates writes queued earlier
 * in the same callback, so it must not share a callback with an add to the
 * same list.
 */
export function clearAnnotations<T extends AnnotationRecord>(
    { snapshot, reset }: Pick<CallbackInterface, 'snapshot' | 'reset'>,
    list: AnnotationListState<T>
): void {
    for (const id of snapshot.getLoadable(list.ids).getValue()) {
        reset(list.item(id));
    }
    reset(list.index);
}

/** The annotation selected on the image, of any kind, or null. */
export const selectedAnnotationState = atom<string | null>({
    key: 'selectedAnnotationState',
    default: null,
});

/** The annotation under the pointer on the image, of any kind, or null. */
export const hoveredAnnotationState = atom<string | null>({
    key: 'hoveredAnnotationState',
    default: null,
});

/** Clears a selection or hover that names `id`. */
export const unlessNaming =
    (id: string) =>
    (named: string | null): string | null =>
        named === id ? null : named;

/** Whether `state` names an id: a shape re-renders only when its own flag flips. */
const namesIdState = (state: RecoilState<string | null>) =>
    selectorFamily<boolean, string>({
        key: `${state.key}NamesId`,
        get:
            (id) =>
            ({ get }) =>
                get(state) === id,
        cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
    });

export const isAnnotationSelectedState = namesIdState(selectedAnnotationState);
export const isAnnotationHoveredState = namesIdState(hoveredAnnotationState);
